'use client'
import { createContext, useContext, useEffect, useState } from 'react'
import type { PinnedMarketEntry } from '@/lib/api'

interface NavContextValue {
  sidebarOpen: boolean
  openSidebar: () => void
  closeSidebar: () => void
  searchOpen: boolean
  openSearch: () => void
  closeSearch: () => void
  pinnedMarkets: PinnedMarketEntry[]
  setPinnedMarkets: (pins: PinnedMarketEntry[]) => void
}

const NavContext = createContext<NavContextValue>({
  sidebarOpen: false,
  openSidebar: () => {},
  closeSidebar: () => {},
  searchOpen: false,
  openSearch: () => {},
  closeSearch: () => {},
  pinnedMarkets: [],
  setPinnedMarkets: () => {},
})

export function NavUIProvider({
  children,
  initialPinnedMarkets = [],
}: {
  children: React.ReactNode
  initialPinnedMarkets?: PinnedMarketEntry[]
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [pinnedMarkets, setPinnedMarkets] = useState<PinnedMarketEntry[]>(initialPinnedMarkets)

  // Cross-root open signal: the embedded casino nav (a separate React root, served by the nav
  // widget) can't share this context, so its mobile SidebarTrigger dispatches a window event
  // that this provider listens for to open the mobile drawer.
  useEffect(() => {
    const open = () => setSidebarOpen(true)
    window.addEventListener('weeabets:open-sidebar', open)
    return () => window.removeEventListener('weeabets:open-sidebar', open)
  }, [])

  // Same cross-root pattern for search: GlobalShortcuts dispatches this instead of calling
  // openSearch() directly, since it may run in the casino's shortcuts widget (a separate React
  // root with no NavUIProvider in scope).
  useEffect(() => {
    const open = () => setSearchOpen(true)
    window.addEventListener('weeabets:open-search', open)
    return () => window.removeEventListener('weeabets:open-search', open)
  }, [])

  return (
    <NavContext.Provider value={{
      sidebarOpen,
      openSidebar: () => setSidebarOpen(true),
      closeSidebar: () => setSidebarOpen(false),
      searchOpen,
      openSearch: () => setSearchOpen(true),
      closeSearch: () => setSearchOpen(false),
      pinnedMarkets,
      setPinnedMarkets,
    }}>
      {children}
    </NavContext.Provider>
  )
}

export function useNavUI(): NavContextValue {
  return useContext(NavContext)
}
